import React from 'react';
import { FileMagnifyingGlassIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import ForecastCard from 'src/components/rebates/ForecastCard';
import RebateCustomBreakdownTableCard from '@/components/rebates/RebateCustomBreakdownTableCard';
import { MultiSelectOption } from '@/components/common/MultiSelect';
import type { FilterGroup } from '../FilterPanelContent';
import {
  DimensionType,
  TableConfig,
  MultiYearForecastData,
} from '@/types/rebateAnalytics';
import { UnifiedForecastResult, RebateRule } from '@erp/shared-types';

export interface StatusTabViewProps {
  // Data
  multiYearForecastData: MultiYearForecastData;
  forecastLoading: boolean;
  hasAllZeroValues: boolean;
  divisionIdToName: Map<string, string>;
  supplierIdToName: Map<string, string>;
  rebateRules?: RebateRule[];

  // Filter state (for FAB)
  filterGroups: FilterGroup[];
  activeFilterCount: number;
  activeFilterNames?: string[];
  onClearAllFilters: () => void;

  // Cumulative toggle
  isCumulative: boolean;
  onCumulativeChange: (cumulative: boolean) => void;

  // Line visibility and primary selection
  visibleLines: Set<string>;
  onVisibleLinesChange: (lines: Set<string>) => void;
  primaryLine: string;
  onPrimaryLineChange: (line: string) => void;

  // Collapse state
  ruleBreakdownCollapsed: boolean;
  onToggleRuleBreakdown: () => void;
  divisionBreakdownCollapsed: boolean;
  onToggleDivisionBreakdown: () => void;

  // Table configs
  ruleBreakdownConfig: TableConfig;
  divisionBreakdownConfig: TableConfig;
  // Update handlers (only for time filter, dimensions are fixed)
  onUpdateRuleBreakdown?: (
    updatedConfig: Omit<TableConfig, 'id' | 'label'>,
  ) => void;
  onUpdateDivisionBreakdown?: (
    updatedConfig: Omit<TableConfig, 'id' | 'label'>,
  ) => void;

  // Admin features
  userIsAdmin: boolean;
  onViewAudit: () => void;

  // Date for date-based calculations (for Storybook/testing)
  currentDate?: Date;

  // Split point calculation (calculated in StatusTab to avoid duplicate calculations)
  predictionSplitDay?: number | null;
  lastRealEstimate?: {
    daysFromStart: number;
    totalRebateAmount: number;
    date: Date;
  } | null;

  // Tag maps for tag-based status cards
  ruleTagsMap?: Map<string, string[]>; // Map of ruleId -> tag names
  tagNameToIdMap?: Map<string, string>; // Map of tag name -> tag ID

  // Reset handler
  onReset: () => void;
  isResetAvailable: boolean;
}

/**
 * Presentational component for StatusTab
 * Pure UI component that receives all data and callbacks as props
 * Suitable for Storybook stories
 */
export const StatusTabView: React.FC<StatusTabViewProps> = ({
  multiYearForecastData,
  forecastLoading,
  hasAllZeroValues,
  divisionIdToName,
  supplierIdToName,
  rebateRules,
  filterGroups,
  activeFilterCount,
  activeFilterNames,
  onClearAllFilters,
  isCumulative,
  onCumulativeChange,
  visibleLines,
  onVisibleLinesChange,
  primaryLine,
  onPrimaryLineChange,
  ruleBreakdownCollapsed,
  onToggleRuleBreakdown,
  divisionBreakdownCollapsed,
  onToggleDivisionBreakdown,
  ruleBreakdownConfig,
  divisionBreakdownConfig,
  onUpdateRuleBreakdown,
  onUpdateDivisionBreakdown,
  userIsAdmin,
  onViewAudit,
  currentDate,
  predictionSplitDay,
  lastRealEstimate,
  ruleTagsMap,
  tagNameToIdMap,
  onReset,
  isResetAvailable,
}) => {
  const transformedForecastData = multiYearForecastData.currentYearData;

  return (
    <div className="space-y-6">
      {/* Forecast Chart with Status Cards */}
      <div className="relative">
        {hasAllZeroValues ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-muted-foreground mb-2">
              No forecast data available
            </h3>
            <p className="text-sm text-muted-foreground">
              All estimates, predictions, and reported data are currently zero.
              Forecast data will appear here once rebate calculations are
              available.
            </p>
          </div>
        ) : transformedForecastData ? (
          <ForecastCard
            multiYearForecastData={multiYearForecastData}
            showStatusCards
            compact
            rebateRules={transformedForecastData.rules}
            isLoadingForecast={forecastLoading}
            isCumulative={isCumulative}
            currentDate={currentDate}
            predictionSplitDay={predictionSplitDay}
            lastRealEstimate={lastRealEstimate}
            ruleTagsMap={ruleTagsMap}
            tagNameToIdMap={tagNameToIdMap}
            visibleLines={visibleLines}
            primaryLine={primaryLine}
            onVisibleLinesChange={onVisibleLinesChange}
          />
        ) : forecastLoading ? (
          <div className="flex justify-center p-6">
            <div className="text-center">
              <Spinner size="lg" className="mx-auto mb-4" />
              <p className="text-sm text-muted-foreground">
                Loading forecast data...
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-muted-foreground mb-2">
              No forecast data available
            </h3>
            <p className="text-sm text-muted-foreground">
              Forecast data will appear here once the agreement period begins
            </p>
          </div>
        )}
      </div>

      {/* Performance Breakdown Tables */}
      {!hasAllZeroValues &&
        transformedForecastData &&
        divisionIdToName.size > 0 && (
          <div className="space-y-6">
            {/* Rule Performance Breakdown */}
            <RebateCustomBreakdownTableCard
              config={ruleBreakdownConfig}
              onUpdate={onUpdateRuleBreakdown}
              multiYearForecastData={multiYearForecastData}
              isCumulative={isCumulative}
              primaryLine={primaryLine}
              supplierIdToName={supplierIdToName}
              divisionIdToName={divisionIdToName}
              isCollapsed={ruleBreakdownCollapsed}
              onToggleCollapse={onToggleRuleBreakdown}
              activeFilterCount={activeFilterCount}
              activeFilterNames={activeFilterNames}
            />

            {/* Division Performance Breakdown */}
            <RebateCustomBreakdownTableCard
              config={divisionBreakdownConfig}
              onUpdate={onUpdateDivisionBreakdown}
              multiYearForecastData={multiYearForecastData}
              isCumulative={isCumulative}
              primaryLine={primaryLine}
              supplierIdToName={supplierIdToName}
              divisionIdToName={divisionIdToName}
              isCollapsed={divisionBreakdownCollapsed}
              onToggleCollapse={onToggleDivisionBreakdown}
              activeFilterCount={activeFilterCount}
              activeFilterNames={activeFilterNames}
            />
          </div>
        )}
    </div>
  );
};
